import React, { useEffect, useState, useCallback } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import DataTable from '../../components/common/DataTable';
import Modal from '../../components/common/Modal';
import FormField from '../../components/common/FormField';
import StatusBadge from '../../components/common/StatusBadge';
import { Loader, ErrorBanner, SuccessBanner } from '../../components/common/Feedback';
import { useAuth } from '../../context/AuthContext';
import * as invoicesApi from '../../api/invoices';
import * as clientsApi from '../../api/clients';
import * as projectsApi from '../../api/projects';
import { formatDate, formatCurrency } from '../../utils/format';

const STATUS_OPTIONS = ['Draft', 'Sent', 'Pending', 'Partially Paid', 'Paid', 'Overdue'].map((s) => ({ value: s, label: s }));

const emptyForm = { clientId: '', projectId: '', invoiceDate: '', dueDate: '', description: '', amount: '', tax: '0', notes: '', status: 'Draft' };

export default function Invoices() {
  const { actor } = useAuth();
  const canManage = actor.role === 'super_admin' || actor.role === 'admin';

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [clientOptions, setClientOptions] = useState([]);
  const [projectOptions, setProjectOptions] = useState([]);

  const [viewing, setViewing] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    invoicesApi
      .listInvoices({})
      .then((res) => setRows(res.data))
      .catch((err) => setError(err.response?.data?.message || 'Failed to load invoices.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const ensureOptionsLoaded = async () => {
    if (clientOptions.length === 0) {
      const res = await clientsApi.listClients({ limit: 500 });
      setClientOptions(res.data.map((c) => ({ value: c.id, label: c.clientName })));
    }
    if (projectOptions.length === 0) {
      const res = await projectsApi.listProjects({ limit: 500 });
      setProjectOptions(res.data.map((p) => ({ value: p.id, label: p.projectName })));
    }
  };

  const openCreate = async () => {
    setForm(emptyForm);
    setEditingId(null);
    await ensureOptionsLoaded();
    setShowForm(true);
  };

  const openEdit = async (row) => {
    setForm({
      clientId: row.clientId,
      projectId: row.projectId || '',
      invoiceDate: row.invoiceDate ? row.invoiceDate.slice(0, 10) : '',
      dueDate: row.dueDate ? row.dueDate.slice(0, 10) : '',
      description: row.description || '',
      amount: row.amount,
      tax: row.tax || '0',
      notes: row.notes || '',
      status: row.status,
    });
    setEditingId(row.id);
    await ensureOptionsLoaded();
    setShowForm(true);
  };

  const openView = async (row) => {
    try {
      const res = await invoicesApi.getInvoice(row.id);
      setViewing(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load invoice.');
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (editingId) {
        await invoicesApi.updateInvoice(editingId, form);
        setSuccess('Invoice updated.');
      } else {
        await invoicesApi.createInvoice(form);
        setSuccess('Invoice created.');
      }
      setShowForm(false);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save invoice.');
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete invoice ${row.invoiceNumber}? This cannot be undone.`)) return;
    try {
      await invoicesApi.deleteInvoice(row.id);
      setSuccess(`Invoice ${row.invoiceNumber} deleted.`);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete invoice.');
    }
  };

  const handleSend = async (row) => {
    try {
      await invoicesApi.sendInvoiceToClient(row.id);
      setSuccess(`Invoice ${row.invoiceNumber} sent to client.`);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send invoice.');
    }
  };

  const handleDownload = async (row) => {
    setError('');
    setDownloadingId(row.id);
    try {
      await invoicesApi.downloadInvoicePdf(row.id, row.invoiceNumber);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to download PDF.');
    } finally {
      setDownloadingId(null);
    }
  };

  const columns = [
    { key: 'invoiceNumber', label: 'Invoice #' },
    { key: 'client', label: 'Client', render: (r) => r.client?.clientName || '-' },
    { key: 'invoiceDate', label: 'Date', render: (r) => formatDate(r.invoiceDate) },
    { key: 'totalAmount', label: 'Total', render: (r) => formatCurrency(r.totalAmount) },
    { key: 'status', label: 'Status', render: (r) => <StatusBadge status={r.status} /> },
    {
      key: 'actions',
      label: 'Actions',
      render: (r) => (
        <div className="btn-group">
          <button type="button" className="btn-link" onClick={() => openView(r)}>View</button>
          <button type="button" className="btn-link" onClick={() => handleDownload(r)} disabled={downloadingId === r.id}>
            {downloadingId === r.id ? 'Downloading...' : 'Download PDF'}
          </button>
          {canManage && (
            <>
              <button type="button" className="btn-link" onClick={() => openEdit(r)}>Edit</button>
              {r.status === 'Draft' && (
                <button type="button" className="btn-link" onClick={() => handleSend(r)}>Send to Client</button>
              )}
              <button type="button" className="btn-link" onClick={() => handleDelete(r)}>Delete</button>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <DashboardLayout title="Invoices">
      <div className="page-header">
        <div>
          <h1 className="heading">Invoices</h1>
          <p className="page-subtext">Billing for audit engagements, with automatic invoice numbering.</p>
        </div>
        {canManage && <button type="button" className="btn" onClick={openCreate}>New Invoice</button>}
      </div>

      <ErrorBanner message={error} />
      <SuccessBanner message={success} />

      {loading ? <Loader text="Loading invoices..." /> : <DataTable columns={columns} rows={rows} getRowKey={(r) => r.id} emptyMessage="No invoices found." />}

      {showForm && (
        <Modal
          title={editingId ? 'Edit Invoice' : 'New Invoice'}
          onClose={() => setShowForm(false)}
          footer={
            <>
              <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
              <button type="submit" form="invoice-form" className="btn">{editingId ? 'Save Changes' : 'Create Invoice'}</button>
            </>
          }
        >
          <form id="invoice-form" onSubmit={handleSave}>
            <div className="form-grid single-column">
              <FormField
                label="Client"
                type="select"
                options={clientOptions}
                value={form.clientId}
                onChange={(e) => setForm({ ...form, clientId: e.target.value })}
                required
                disabled={!!editingId}
              />
              <FormField label="Project (optional)" type="select" options={projectOptions} value={form.projectId} onChange={(e) => setForm({ ...form, projectId: e.target.value })} />
              <FormField label="Invoice Date" type="date" value={form.invoiceDate} onChange={(e) => setForm({ ...form, invoiceDate: e.target.value })} required />
              <FormField label="Due Date" type="date" value={form.dueDate} onChange={(e) => setForm({ ...form, dueDate: e.target.value })} />
              <FormField label="Description" type="textarea" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              <FormField label="Amount" type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required />
              <FormField label="Tax" type="number" step="0.01" value={form.tax} onChange={(e) => setForm({ ...form, tax: e.target.value })} />
              {editingId && (
                <FormField label="Status" type="select" options={STATUS_OPTIONS} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} />
              )}
              <FormField label="Notes" type="textarea" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </form>
        </Modal>
      )}

      {viewing && (
        <Modal
          title={`Invoice ${viewing.invoiceNumber}`}
          onClose={() => setViewing(null)}
          footer={<button type="button" className="btn" onClick={() => setViewing(null)}>Close</button>}
        >
          <div className="detail-grid">
            <div><strong>Client:</strong> {viewing.client?.clientName || '-'}</div>
            <div><strong>Project:</strong> {viewing.project?.projectName || '-'}</div>
            <div><strong>Invoice Date:</strong> {formatDate(viewing.invoiceDate)}</div>
            <div><strong>Due Date:</strong> {viewing.dueDate ? formatDate(viewing.dueDate) : '-'}</div>
            <div><strong>Status:</strong> <StatusBadge status={viewing.status} /></div>
            <div><strong>Description:</strong> {viewing.description || '-'}</div>
            <div><strong>Amount:</strong> {formatCurrency(viewing.amount)}</div>
            <div><strong>Tax:</strong> {formatCurrency(viewing.tax)}</div>
            <div><strong>Total:</strong> {formatCurrency(viewing.totalAmount)}</div>
            <div><strong>Notes:</strong> {viewing.notes || '-'}</div>
          </div>
        </Modal>
      )}
    </DashboardLayout>
  );
}
